<template>
	<div>
		<div>
			<h5 class="text-h5 q-mt-lg q-mb-md">{{ $t(game_data.game_name) }}</h5>

			<q-collapsible
				class="shadow-2 q-pa-sm q-ma-xs rounded-border"
				icon="far fa-check-square"
				right-icon="far fa-check-square"
				:label="$t('how_to_create_the_game')"
			>
				<div class="q-ma-sm">
					<p>{{ $t('enigma_instructions') }}</p>
					<p class="q-mt-lg">
						<em
							><strong>{{ `${$t('warning')}: ` }}</strong
							>{{ $t('create_game_before_save') }}</em
						>
					</p>
				</div>
			</q-collapsible>

			<div class="row">
				<div class="col-12 q-mb-md">
					<div class="row gutter-sm">
						<div v-if="game_data.options.length" class="col-12">
							<div v-for="(option, index) in game_data.options" :key="index">
								<q-list dense bordered padding separator class="q-mt-lg">
									<q-item>
										<q-item-main class="q-mx-sm q-mt-sm">
											<div class="row justify-between">
												<q-item-main
													:label="`${$t('enigma')} ${index + 1}`"
												/>
												<q-item-main>
													<q-btn
														v-show="game_data.options.length > 2"
														dense
														outline
														color="negative"
														@click="removeRiddle(index)"
														size="md"
													>
														{{ $t('remove') }}
														<q-tooltip>
															{{ $t('remove_enigma') }}
														</q-tooltip>
													</q-btn>
												</q-item-main>
												<q-btn
													v-show="option.tips.length < 3"
													dense
													outline
													round
													@click="addTip(index)"
													icon="fas fa-plus"
													size="md"
													><q-tooltip>
														{{ $t('add_tip') }}
													</q-tooltip>
												</q-btn>
											</div>

											<div
												class="row justify-center items-center"
												v-for="(tip, tipIdx) in option.tips"
												:key="tipIdx"
											>
												<q-field :count="120" class="col-11">
													<q-input
														v-model="tip.label"
														type="textarea"
														rows="2"
														:max-height="120"
														:float-label="$t('tip')"
														@change="game_built ? resetGame() : null"
														:error="
															$v.game_data.options.$each[index].tips
																.$each[tipIdx].label.$error
														"
														@input="
															$v.game_data.options.$each[index].tips
																.$each[tipIdx].label.$touch;
														"
													/>
												</q-field>
												<div class="justify-center q-pl-lg">
													<q-btn
														v-if="option.tips.length > 2"
														round
														outline
														color="negative"
														icon="fas fa-trash"
														@click="removeTip(index, tipIdx)"
													>
														<q-tooltip>
															{{ $t('remove_tip') }}
														</q-tooltip>
													</q-btn>
												</div>
											</div>
										</q-item-main>
									</q-item>

									<q-item-separator inset />

									<q-item class="column col-12 q-mx-sm q-mt-sm">
										<div class="full-width row justify-between">
											<small>{{ $t('alternatives') }}</small>
											<q-btn
												v-show="option.alternatives.length < 5"
												dense
												outline
												round
												@click="addAlternative(index)"
												icon="fas fa-plus"
												size="md"
												><q-tooltip>
													{{ $t('add_alternative') }}
												</q-tooltip>
											</q-btn>
										</div>

										<div class="full-width row justify-start q-mt-md">
											<q-field
												v-for="(alternative, altIdx) in option.alternatives"
												:key="altIdx"
												:count="50"
												class="col-xs-12 col-md-4 col-4 q-px-lg q-pt-md"
											>
												<div class="row justify-between items-center">
													<q-item-side>
														<q-radio
															v-model="option.correct_choice"
															:val="alternative.value"
															:color="
																option.correct_choice ===
																alternative.value
																	? 'positive'
																	: ''
															"
															size="md"
															:label="$t('correct')"
														/>
													</q-item-side>
													<q-item-side right>
														<q-btn
															v-if="option.alternatives.length > 3"
															round
															outline
															dense
															color="negative"
															icon="fas fa-trash"
															@click="
																removeAlternative(index, altIdx)
															"
														>
															<q-tooltip>
																{{ $t('remove_alternative') }}
															</q-tooltip>
														</q-btn>
													</q-item-side>
												</div>
												<q-input
													v-model="alternative.label"
													type="text"
													rows="2"
													:float-label="
														`${$t('alternative')} ${altIdx + 1}`
													"
													@change="game_built ? resetGame() : null"
													:error="
														$v.game_data.options.$each[index]
															.alternatives.$each[altIdx].label.$error
													"
													@input="
														$v.game_data.options.$each[index]
															.alternatives.$each[altIdx].label
															.$touch;
													"
												/>
											</q-field>
										</div>
									</q-item>
								</q-list>
							</div>
						</div>

						<div class="col-xs-12 q-mt-sm q-mx-xs">
							<q-field :helper="$t('leave_it_blank_or_negative_to_use_infinite')">
								<q-input
									v-model="game_data.max_errors"
									type="number"
									:float-label="$t('alternative_error_limit')"
									@change="game_built ? resetGame() : null"
								/>
							</q-field>
						</div>

						<div class="full-width row justify-end q-mr-md q-mt-md">
							<q-btn
								class="q-mr-md"
								color="primary"
								icon="fas fa-plus"
								:label="$t('add')"
								:disable="game_data.options.length > 9"
								@click="addRiddle"
							>
								<q-tooltip>
									{{ $t('add_enigma') }}
								</q-tooltip>
							</q-btn>
							<q-btn
								color="secondary"
								:label="$t('preview')"
								@click="buildGamePreview"
								:disable="disableGamePreview"
							>
								<q-tooltip>
									{{ $t('preview_game') }}
								</q-tooltip>
							</q-btn>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- preview -->
		<enigma-preview
			v-if="show_game_preview"
			:show_game_preview="show_game_preview"
			@hide_preview="hidePreview()"
			:game_name="game_data.game_name"
			:game_options="game_data.options"
			:game_max_errors="game_data.max_errors"
		/>
	</div>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import EnigmaPreview from './EnigmaPreview.vue'

export default {
	name: 'AdminGameEnigma',

	props: ['can_save', 'saved_game_data', 'id_game'],

	components: {
		'enigma-preview': EnigmaPreview,
	},

	data() {
		return {
			game_data: {
				game_name: 'enigma',
				max_errors: null,
				options: [
					{
						tips: [{ label: '' }, { label: '' }],
						alternatives: [
							{ label: '', value: 1 },
							{ label: '', value: 2 },
							{ label: '', value: 3 },
						],
						correct_choice: 3,
						correct: undefined,
						position: 0,
						selected: undefined,
					}
				],
			},
			show_game_preview: false,
			game_built: false,
		}
	},

	computed: {
		disableGamePreview() {
			if (this.game_data.options.length < 1 || this.game_data.options.length > 10) return true
		},
	},

	methods: {
		buildGamePreview() {
			this.$v.$touch()
			if (!this.$v.$error) {
				this.show_game_preview = true
				this.game_built = true
				this.$emit('save_toggle', this.game_built)
				this.$emit('save_game_data', this.game_data)
			}
		},

		addTip(idx) {
			if (this.game_data.options[idx].tips.length > 2) return

			this.game_data.options[idx].tips.push({ label: '' })
		},

		addAlternative(idx) {
			if (this.game_data.options[idx].alternatives.length > 4) return

			this.game_data.options[idx].alternatives.push({
				label: '',
				value: this.game_data.options[idx].alternatives.length + 1,
			})
		},

		removeTip(optIdx, tipIdx) {
			this.game_data.options[optIdx].tips.splice(tipIdx, 1)
		},

		removeAlternative(optIdx, altIdx) {
			this.game_data.options[optIdx].alternatives.splice(altIdx, 1)
		},

		addRiddle() {
			const newRiddle = {
				tips: [{ label: '' }, { label: '' }],
				alternatives: [
					{ label: '', value: 1 },
					{ label: '', value: 2 },
					{ label: '', value: 3 },
				],
				correct_choice: 1,
				correct: undefined,
				position: this.game_data.options.length + 1,
				selected: null,
			}

			this.game_data.options.push(newRiddle)
		},

		removeRiddle(idx) {
			this.game_data.options.splice(idx, 1)
		},

		resetGame() {
			this.game_built = false
			this.$emit('save_toggle', this.game_built)
			this.$emit('save_game_data', null)
		},

		hidePreview() {
			this.show_game_preview = false
		},
	},

	mounted() {
		if (this.saved_game_data) {
			this.game_data = this.saved_game_data
		} else {
			return
		}
	},

	beforeDestroy() {
		this.resetGame()
	},

	validations() {
		return {
			game_data: {
				options: {
					$each: {
						tips: {
							$each: {
								label: { required, maxLength: maxLength(120) },
							},
						},
						alternatives: {
							$each: {
								label: { required, maxLength: maxLength(50) },
							},
						},
					},
				},
			},
		}
	},
}
</script>

<style></style>
